import React, { useState } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
  Alert,
  Stack,
  IconButton,
  Tooltip,
  Tab,
  Tabs,
} from '@mui/material';
import {
  Help as HelpIcon,
  ExpandMore as ExpandMoreIcon,
  Support as SupportIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Chat as ChatIcon,
  VideoLibrary as VideoIcon,
  MenuBook as GuideIcon,
  BugReport as BugIcon,
  Feedback as FeedbackIcon,
  School as TutorialIcon,
  QuestionAnswer as FAQIcon,
  ContactSupport as ContactIcon,
  Launch as LaunchIcon,
  Search as SearchIcon,
  Star as StarIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  AccountTree as FlowIcon,
  Code as CodeIcon,
  Dashboard as DashboardIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getBrand } from '../config/brand.js';

function HelpSupport({ darkMode }) {
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState(0);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    priority: 'medium',
  });
  const [feedbackForm, setFeedbackForm] = useState({
    rating: 5,
    category: 'general',
    message: '',
  });

  const faqData = [
    {
      category: 'Getting Started',
      questions: [
        {
          question: 'How do I sign up for a new company account?',
          answer: 'Click "Get Started" on the landing page to create a new company account. You\'ll need to provide company information, contact details, and create your first admin user. Your account will be reviewed and approved within 24-48 hours.',
        },
        {
          question: 'How do I join an existing company?',
          answer: 'Click "Join Company" on the landing page to register as a user for an existing approved company. Select your company from the dropdown and fill out your personal information. You\'ll have immediate access once registered.',
        },
        {
          question: 'What is the difference between customer signup and tenant user signup?',
          answer: 'Customer signup creates a new company (tenant) and requires approval. Tenant user signup is for joining existing approved companies. The first user of a company becomes an admin, while additional users get standard user roles.',
        },
        {
          question: 'How do I create my first form?',
          answer: 'Navigate to the Form Builder from the dashboard or menu. Drag and drop form elements from the widget panel to create your form. You can preview your form in real-time and publish it when ready.',
        },
        {
          question: 'What is an Agentic Flow?',
          answer: 'An Agentic Flow is a multi-agent workflow system that allows you to create automated processes using AI agents. Each agent can perform specific tasks and collaborate with other agents to complete complex workflows.',
        },
      ],
    },
    {
      category: 'Agentic Workflows',
      questions: [
        {
          question: 'What are Agentic Workflows?',
          answer: 'Agentic Workflows are intelligent automation systems that combine AI agents, human input, and business logic to create sophisticated workflows. They can handle complex tasks like data processing, decision-making, and multi-step operations.',
        },
        {
          question: 'What node types are supported?',
          answer: 'The system supports multiple node types: AI Agent (LLM processing), Code Executor (Python code), API Call (HTTP requests), RAG (document retrieval), Form (human input), Conditional (decision logic), Loop (iterations), Parallel (concurrent execution), Join (merge results), Email (communications), Calendar (scheduling), Transform (data conversion), Start/End (flow control).',
        },
        {
          question: 'What edge types are available?',
          answer: 'Edge types include: Basic (simple connection), Conditional (condition-based routing), On Success (execute on success), On Error (execute on failure), Always (always execute), Parallel (concurrent paths), Join (wait for parallel completion), Loop (iteration control), Timeout (time-based execution), Retry (retry on failure).',
        },
        {
          question: 'How do conditional edges work?',
          answer: 'Conditional edges evaluate expressions using the current state. They can check conditions like "state.get(\'input\', \'\').length > 10" or "state.get(\'ai_response\') is not None". If the condition is true, the edge is traversed; otherwise, execution follows alternative paths.',
        },
        {
          question: 'How do On Success and On Error edges work?',
          answer: 'On Success edges only execute when the previous node completes successfully (no errors). On Error edges only execute when the previous node fails (has an error). This allows you to create error handling and success paths in your workflows.',
        },
        {
          question: 'Is the Form node a human-in-the-loop feature?',
          answer: 'Yes! The Form node is a key human-in-the-loop feature. It allows workflows to pause and collect human input during execution. Forms can be static (predefined fields) or dynamic (configurable fields). This enables workflows that require human decision-making, approval, or data entry.',
        },
        {
          question: 'How do I use state variables in workflows?',
          answer: 'State variables store data that flows between nodes. You can create variables with different scopes (global, flow, node) and data types (string, number, boolean, object, array). Use {{variable}} syntax in node configurations to reference state variables.',
        },
        {
          question: 'How do I create parallel execution paths?',
          answer: 'Use Parallel nodes to split execution into multiple concurrent paths. Each branch executes independently, and a Join node waits for all branches to complete before continuing. This is useful for processing multiple tasks simultaneously.',
        },
        {
          question: 'How do I handle errors in workflows?',
          answer: 'Use On Error edges to create error handling paths. When a node fails, execution follows error edges to recovery or notification nodes. You can also use Try-Catch nodes for more sophisticated error handling.',
        },
        {
          question: 'How do I use the Code Executor node?',
          answer: 'The Code Executor runs Python code with access to the current state. You can manipulate data, perform calculations, call external APIs, and update state variables. The code runs in a sandboxed environment for security.',
        },
        {
          question: 'How do I integrate external APIs?',
          answer: 'Use the API Call node to make HTTP requests to external services. Configure the URL, method, headers, and body. You can use state variables in URLs and request bodies with {{variable}} syntax.',
        },
        {
          question: 'How do I use RAG (Retrieval Augmented Generation)?',
          answer: 'The RAG node connects to your document collection to retrieve relevant information. It can upload documents, query the knowledge base, and provide context for AI responses. This is useful for workflows that need to reference specific documents or knowledge.',
        },
        {
          question: 'How do I schedule workflows?',
          answer: 'Use the Calendar node to create, read, update, or delete calendar events. This enables workflows that trigger based on schedules, send meeting invitations, or manage appointments automatically.',
        },
        {
          question: 'How do I monitor workflow execution?',
          answer: 'The Execution Tracker provides detailed logs of workflow execution, including step-by-step progress, timing, state changes, and error information. You can view execution history and debug workflow issues.',
        },
        {
          question: 'Can I use workflows with the chat interface?',
          answer: 'Yes! You can select workflows from the chat interface to execute them with your input. The workflow processes your request and returns results through the chat, making it easy to interact with complex automation.',
        },
      ],
    },
    {
      category: 'MCP Integration',
      questions: [
        {
          question: 'What is MCP (Model Context Protocol)?',
          answer: 'MCP is a protocol that allows AI assistants to connect to external tools and services. Our platform integrates MCP to provide seamless access to your agentic flows as tools and connect to external MCP servers.',
        },
        {
          question: 'How do I use my agentic flows as MCP tools?',
          answer: 'Your agentic flows are automatically exposed as MCP tools. In the chat interface, open the Form Options Drawer, select an MCP server, and choose from your available flows in the MCP Tools dropdown. Each flow becomes a tool you can execute directly in conversations.',
        },
        {
          question: 'How do I connect to external MCP servers?',
          answer: 'Go to the MCP Dashboard (accessible from the navigation menu), click "Add Server", and enter the server details (name, host, port, authentication). The system will automatically test the connection and load available tools.',
        },
        {
          question: 'What MCP servers are supported?',
          answer: 'We support any MCP-compliant server. Popular examples include file system servers, database servers, web search servers, and custom business logic servers. The system automatically detects and loads available tools from connected servers.',
        },
        {
          question: 'How do I manage MCP server connections?',
          answer: 'Use the MCP Dashboard to view all connected servers, test connections, update server settings, and remove servers. Server configurations are automatically saved and persist across sessions.',
        },
        {
          question: 'Can I use multiple MCP tools in one conversation?',
          answer: 'Yes! You can combine multiple MCP tools in a single conversation. Select different tools from the MCP Tools dropdown as needed, and the AI will use them together to provide comprehensive responses.',
        },
        {
          question: 'How do I troubleshoot MCP connection issues?',
          answer: 'Check the MCP Dashboard for connection status indicators. Red indicators show connection problems. Verify server URLs, ports, and authentication credentials. Use the "Test Connection" feature to diagnose issues.',
        },
        {
          question: 'Are my MCP server configurations secure?',
          answer: 'Yes, all MCP server configurations are encrypted and stored securely. Authentication tokens and credentials are protected using industry-standard encryption methods.',
        },
        {
          question: 'Can I create custom MCP tools?',
          answer: 'Yes! Your agentic flows automatically become MCP tools. You can also create custom MCP servers using our Python backend framework to expose additional functionality as tools.',
        },
        {
          question: 'How do MCP tools integrate with forms?',
          answer: 'MCP tools can be used within form workflows. When a form requires data processing or external service calls, MCP tools can be invoked to enhance the form\'s capabilities with external functionality.',
        },
      ],
    },
    {
      category: 'Account Management',
      questions: [
        {
          question: 'How do I check my account approval status?',
          answer: 'After signing up for a new company, you\'ll be redirected to the pending approval page. You can check your status there or contact support if you need assistance.',
        },
        {
          question: 'What roles are available in the system?',
          answer: 'The system supports multiple roles: Super Admin (manages all tenants), Admin (manages their tenant), Manager (extended permissions), User (standard access), and Viewer (read-only access).',
        },
        {
          question: 'How do I manage users in my company?',
          answer: 'Admins can manage users through the User Management section in Settings. You can invite new users, assign roles, and manage permissions for your company.',
        },
        {
          question: 'How do I reset my password?',
          answer: 'Click "Forgot Password?" on the signin page. Enter your email address and we\'ll send you a secure link to reset your password.',
        },
        {
          question: 'Can I change my company information?',
          answer: 'Company admins can update company information through the Settings section. Contact support if you need to make significant changes to your company profile.',
        },
      ],
    },
    {
      category: 'CRM Features',
      questions: [
        {
          question: 'What CRM features are available?',
          answer: 'The CRM includes contact management, property listings, opportunities, tasks, notes, lead stages, notifications, AI fields, and campaigns. All features are mobile-responsive and optimized for small screens.',
        },
        {
          question: 'How do I add a new contact?',
          answer: 'Navigate to CRM > Contacts and click the "+" button. Fill out the contact form with personal and company information. You can also import contacts via CSV.',
        },
        {
          question: 'How do I manage properties?',
          answer: 'Go to CRM > Properties to view and manage property listings. You can add new properties, update details, and track property status.',
        },
        {
          question: 'What are AI Fields?',
          answer: 'AI Fields use artificial intelligence to automatically extract and categorize information from your data, making it easier to organize and search through your CRM records.',
        },
        {
          question: 'How do I track opportunities?',
          answer: 'Navigate to CRM > Opportunities to create and track sales opportunities. You can assign stages, set values, and track progress through your sales pipeline.',
        },
      ],
    },
    {
      category: 'App Builder',
      questions: [
        {
          question: 'What is the App Builder?',
          answer: 'The App Builder lets you create custom applications that combine AI chat with data panels (tables, charts, forms). Each app connects to your agentic flows and displays their outputs in a flexible layout.',
        },
        {
          question: 'How do I create a new app?',
          answer: 'Go to App Builder, click "New App" for a blank app, or "From template" to start from Lead Qualifier, FAQ Bot, Data Dashboard, Minimal Chat, or Blank. Then click Edit to configure panels and settings.',
        },
        {
          question: 'What are panels and how do I configure them?',
          answer: 'Panels are the left, center, and right columns. The center is usually chat. Left/right panels show tables, charts, stats, or forms. Click a panel in edit mode to set its flow, output key (e.g. table_data, chart_data), and component type.',
        },
        {
          question: 'What layouts are available?',
          answer: 'Two Panel (left + right only), Three Panel (left | chat | right), and Dynamic (multiple panels per side). You can also choose Chat position: Center (chat-focused) or Header (compact bar at top with session summary in center).',
        },
        {
          question: 'How do I publish or embed my app?',
          answer: 'In Edit mode, open Settings in the right drawer. Under "Publish & embed" you\'ll find the public run-only URL (/p/your-app-id) and an iframe snippet. Copy and paste into your website or share the URL.',
        },
        {
          question: 'Can I customize the look of my app?',
          answer: 'Yes. In Settings, use "Branding (theme)" to set a Primary color and Logo URL. The theme applies when users run or embed the app.',
        },
        {
          question: 'How do I undo changes?',
          answer: 'Use the Undo and Redo buttons in the edit toolbar. They apply to panel edits within the current session (add, remove, or change panel config).',
        },
        {
          question: 'How do I restore a previous version?',
          answer: 'In Settings, scroll to "Version history". Each save creates a snapshot (last 20 kept). Click "Restore" next to a version to roll back.',
        },
        {
          question: 'How do I duplicate an app?',
          answer: 'On the App Builder list, click the copy icon on any app row. A duplicate is created with "(Copy)" in the name. You can then edit and customize it.',
        },
        {
          question: 'Can I preview my app on phone or tablet?',
          answer: 'Yes. In edit mode, use the device preview icons (phone, tablet, desktop) in the toolbar to see how your layout looks at different screen sizes.',
        },
        {
          question: 'What output keys do flows need to return?',
          answer: 'Each panel type expects specific keys: table_data for tables ({ columns, rows }), chart_data for charts ({ labels, datasets }), stats_data for stats, rich_text for rich text. Configure the output key in each panel\'s properties.',
        },
      ],
    },
    {
      category: 'Form Builder',
      questions: [
        {
          question: 'What types of form elements are available?',
          answer: 'The Form Builder supports various elements including text inputs, numbers, checkboxes, radio buttons, dropdowns, date pickers, file uploads, grids, and group boxes. Each element can be customized with validation rules and styling.',
        },
        {
          question: 'Can I add custom validation to form fields?',
          answer: 'Yes! Each form element supports custom validation including required fields, minimum/maximum lengths, pattern matching (regex), and custom error messages.',
        },
        {
          question: 'How do I view form submissions?',
          answer: 'Published forms collect submissions that you can view in the Form Analytics section. You can export data, view individual submissions, and track form performance.',
        },
        {
          question: 'How do I publish a form?',
          answer: 'After creating your form in the Form Builder, click the "Save" button and then use the "Publish" option. Published forms become available for public submissions and can be accessed via a unique URL.',
        },
        {
          question: 'Can I create mobile-responsive forms?',
          answer: 'Yes! All forms are automatically mobile-responsive and work perfectly on smartphones and tablets. The Form Builder includes mobile preview options.',
        },
      ],
    },
    {
      category: 'Agent Management',
      questions: [
        {
          question: 'How do I create an AI agent?',
          answer: 'Navigate to Agent Manager and click "Create Agent". Choose from predefined templates or create a custom agent with specific capabilities and instructions.',
        },
        {
          question: 'What can AI agents do?',
          answer: 'AI agents can handle customer inquiries, process data, automate workflows, generate reports, and perform various business tasks based on your configuration.',
        },
        {
          question: 'How do I manage agent groups?',
          answer: 'Use the Agent Groups feature to organize agents by function or department. This helps with collaboration and workflow management.',
        },
        {
          question: 'Can I customize agent responses?',
          answer: 'Yes! You can customize agent personalities, response styles, and knowledge bases to match your business needs and brand voice.',
        },
      ],
    },
    {
      category: 'Tenant Management (Super Admin)',
      questions: [
        {
          question: 'How do I approve new tenant applications?',
          answer: 'Navigate to Settings > Tenant Approval to review pending applications. You can approve or reject applications with comments. Approved tenants can immediately start using the platform.',
        },
        {
          question: 'What information do I need to approve a tenant?',
          answer: 'Review the company information, contact details, industry, and company size. You can also check their website and business description to make an informed decision.',
        },
        {
          question: 'How do I manage existing tenants?',
          answer: 'Use the Tenant Management section to view all tenants, configure LLM API keys, update settings, and manage tenant-specific configurations.',
        },
        {
          question: 'Can I suspend or delete tenants?',
          answer: 'Yes, super admins can suspend or delete tenants through the Tenant Management interface. This action affects all users within that tenant.',
        },
      ],
    },
    {
      category: 'Security & Permissions',
      questions: [
        {
          question: 'How secure is my data?',
          answer: 'Your data is protected with enterprise-grade security including encryption, role-based access control, and tenant isolation. Each company\'s data is completely separated.',
        },
        {
          question: 'What permissions do different roles have?',
          answer: 'Super Admins have full system access. Admins can manage their tenant and users. Managers have extended permissions. Users have standard access. Viewers have read-only access.',
        },
        {
          question: 'How do I manage API keys?',
          answer: 'Admins can configure LLM API keys in the Tenant Management section. These keys are used for AI features and are stored securely.',
        },
        {
          question: 'Can I audit user activities?',
          answer: 'Yes, the system provides comprehensive audit logs for user activities, data access, and system changes.',
        },
      ],
    },
    {
      category: 'Mobile & Accessibility',
      questions: [
        {
          question: 'Is the platform mobile-friendly?',
          answer: 'Yes! The entire platform is fully responsive and optimized for mobile devices. All features work seamlessly on smartphones and tablets.',
        },
        {
          question: 'Can I use the platform offline?',
          answer: 'Some features work offline, but most require an internet connection for real-time data synchronization and AI features.',
        },
        {
          question: 'Are there mobile-specific features?',
          answer: 'Yes, the mobile interface includes touch-optimized controls, swipe gestures, and mobile-specific navigation patterns.',
        },
      ],
    },
    {
      category: 'Billing & Subscriptions',
      questions: [
        {
          question: 'What subscription plans are available?',
          answer: 'We offer trial, basic, professional, and enterprise plans. New companies start with a 30-day free trial. Contact sales for enterprise pricing.',
        },
        {
          question: 'How do I upgrade my plan?',
          answer: 'Contact your account manager or support team to discuss plan upgrades and pricing options.',
        },
        {
          question: 'What happens when my trial expires?',
          answer: 'You\'ll receive notifications before your trial expires. You can upgrade to a paid plan to continue using all features.',
        },
      ],
    },
  ];

  const supportOptions = [
    {
      title: 'Email Support',
      description: 'Get help via email within 24 hours',
      icon: <EmailIcon />,
      action: () => setContactDialogOpen(true),
      color: 'primary',
    },
    {
      title: 'Live Chat',
      description: 'Chat with our support team in real-time',
      icon: <ChatIcon />,
      action: () => window.open(typeof window !== 'undefined' ? window.location.origin : 'https://chat.baiglabs.com', '_blank'),
      color: 'success',
    },
    {
      title: 'Phone Support',
      description: 'Call us for urgent issues',
      icon: <PhoneIcon />,
      action: () => window.open('tel:+1-555-0123'),
      color: 'info',
    },
    {
      title: 'Video Tutorials',
      description: 'Learn with step-by-step video guides',
      icon: <VideoIcon />,
      action: () => (getBrand() === 'interviewflow' ? navigate('/demo') : window.open('https://docs.baiglabs.com/tutorials', '_blank')),
      color: 'warning',
    },
    {
      title: 'Documentation',
      description: getBrand() === 'interviewflow' ? 'Flow Builder and guided chat guides' : 'Comprehensive guides and API docs',
      icon: <GuideIcon />,
      action: () => (getBrand() === 'interviewflow' ? navigate('/agentic-workflow-help') : window.open('https://docs.baiglabs.com', '_blank')),
      color: 'secondary',
    },
    {
      title: 'Community Forum',
      description: 'Connect with other users',
      icon: <FAQIcon />,
      action: () => window.open(getBrand() === 'interviewflow' ? 'https://baiglabs.com' : 'https://community.baiglabs.com', '_blank'),
      color: 'success',
    },
  ];

  const quickActions = [
    {
      title: 'Create Your First Form',
      description: 'Step-by-step guide to building forms',
      icon: <GuideIcon />,
      action: () => navigate('/formBuilder'),
      color: 'primary',
    },
    {
      title: 'Set Up CRM',
      description: 'Configure your CRM workspace',
      icon: <BusinessIcon />,
      action: () => navigate('/crm'),
      color: 'success',
    },
    {
      title: 'Create AI Agent',
      description: 'Build your first AI agent',
      icon: <SupportIcon />,
      action: () => navigate('/agent-manager'),
      color: 'warning',
    },
    {
      title: 'Agentic Workflow Help',
      description: 'Comprehensive guide to agentic workflows',
      icon: <FlowIcon />,
      action: () => navigate('/agentic-workflow-help'),
      color: 'secondary',
    },
    {
      title: 'App Builder',
      description: 'Create apps that combine chat with data panels',
      icon: <DashboardIcon />,
      action: () => navigate('/dashboard-builder'),
      color: 'secondary',
    },
    {
      title: 'MCP Help',
      description: 'Connect your flows to AI clients via MCP',
      icon: <CodeIcon />,
      action: () => navigate('/mcp-help'),
      color: 'info',
    },
    {
      title: 'Invite Team Members',
      description: 'Add users to your workspace',
      icon: <PersonIcon />,
      action: () => navigate('/user-management'),
      color: 'info',
    },
  ];

  const handleContactSubmit = () => {
    // Implement contact form submission
    console.log('Contact form:', contactForm);
    setContactDialogOpen(false);
    setContactForm({
      name: '', email: '', subject: '', message: '', priority: 'medium',
    });
  };

  const handleFeedbackSubmit = () => {
    // Implement feedback submission
    console.log('Feedback:', feedbackForm);
    setFeedbackDialogOpen(false);
    setFeedbackForm({ rating: 5, category: 'general', message: '' });
  };

  const filteredFAQs = faqData.map((category) => ({
    ...category,
    questions: category.questions.filter(
      (q) => q.question.toLowerCase().includes(searchQuery.toLowerCase())
        || q.answer.toLowerCase().includes(searchQuery.toLowerCase()),
    ),
  })).filter((category) => category.questions.length > 0);

  return (
    <Box sx={{ p: 3, bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            display: 'flex', alignItems: 'center', gap: 1, mb: 1,
          }}
        >
          <HelpIcon />
          {' '}
          Help & Support
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Find answers, get support, and learn how to make the most of our platform.
        </Typography>
      </Box>

      {/* Tab Navigation */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={selectedTab}
          onChange={(e, newValue) => setSelectedTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="FAQ" icon={<FAQIcon />} iconPosition="start" />
          <Tab label="Support" icon={<SupportIcon />} iconPosition="start" />
          <Tab label="Quick Actions" icon={<LaunchIcon />} iconPosition="start" />
          <Tab label="Contact Us" icon={<ContactIcon />} iconPosition="start" />
          <Tab label="Feedback" icon={<FeedbackIcon />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* FAQ Tab */}
      {selectedTab === 0 && (
        <Box>
          {/* Search */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Search FAQ..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Box>

          {/* Recent Updates Alert */}
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              🎉 Recent Updates
            </Typography>
            <Typography variant="body2">
              <strong>Customer Onboarding System:</strong>
              {' '}
              New streamlined signup process with tenant approval workflow.
              <strong>Enhanced CRM:</strong>
              {' '}
              Mobile-responsive design with improved contact management, properties, and AI fields.
              <strong>Tenant Management:</strong>
              {' '}
              Super admin tools for managing companies and user access.
            </Typography>
          </Alert>

          {/* FAQ Accordion */}
          <Accordion>
            {faqData
              .filter((category) => searchQuery === ''
                || category.category.toLowerCase().includes(searchQuery.toLowerCase())
                || category.questions.some((q) => q.question.toLowerCase().includes(searchQuery.toLowerCase())
                  || q.answer.toLowerCase().includes(searchQuery.toLowerCase())))
              .map((category, categoryIndex) => (
                <Accordion key={categoryIndex}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {category.category}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={2}>
                      {category.questions.map((faq, faqIndex) => (
                        <Box key={faqIndex}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                            {faq.question}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {faq.answer}
                          </Typography>
                          {faqIndex < category.questions.length - 1 && <Divider sx={{ mt: 2 }} />}
                        </Box>
                      ))}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              ))}
          </Accordion>
        </Box>
      )}

      {/* Support Tab */}
      {selectedTab === 1 && (
        <Box>
          <Grid container spacing={3}>
            {supportOptions.map((channel, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card sx={{ height: '100%', cursor: 'pointer' }} onClick={channel.action}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box sx={{
                        bgcolor: `${channel.color}.main`,
                        color: `${channel.color}.contrastText`,
                        p: 1,
                        borderRadius: 1,
                        mr: 2,
                      }}
                      >
                        {channel.icon}
                      </Box>
                      <Typography variant="h6">{channel.title}</Typography>
                    </Box>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {channel.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Contact Information */}
          <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Contact Information</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <EmailIcon sx={{ mr: 1 }} />
                  <Typography>support@company.com</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PhoneIcon sx={{ mr: 1 }} />
                  <Typography>+1-800-SUPPORT</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <LocationIcon sx={{ mr: 1 }} />
                  <Typography>San Francisco, CA</Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      )}

      {/* Resources Tab */}
      {selectedTab === 2 && (
        <Grid container spacing={3}>
          {quickActions.map((action, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {action.icon}
                    <Typography variant="h6" sx={{ ml: 1 }}>
                      {action.title}
                    </Typography>
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {action.description}
                  </Typography>

                  <Button
                    variant="contained"
                    onClick={action.action}
                    sx={{ width: '100%' }}
                  >
                    {action.title}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Feedback Tab */}
      {selectedTab === 3 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Share Your Feedback</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Help us improve by sharing your thoughts, suggestions, or reporting issues.
          </Typography>

          <Button
            variant="contained"
            onClick={() => setFeedbackDialogOpen(true)}
            sx={{ mb: 2 }}
          >
            Submit Feedback
          </Button>

          <Alert severity="info" sx={{ mt: 2 }}>
            Your feedback is valuable to us! We read every submission and use your input to improve our platform.
          </Alert>
        </Paper>
      )}

      {/* Contact Dialog */}
      <Dialog open={contactDialogOpen} onClose={() => setContactDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Contact Support</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              value={contactForm.name}
              onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Email"
              type="email"
              value={contactForm.email}
              onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Subject"
              value={contactForm.subject}
              onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Priority"
              select
              value={contactForm.priority}
              onChange={(e) => setContactForm({ ...contactForm, priority: e.target.value })}
              fullWidth
              SelectProps={{ native: true }}
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </TextField>
            <TextField
              label="Message"
              multiline
              rows={4}
              value={contactForm.message}
              onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
              fullWidth
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleContactSubmit} variant="contained">Send Message</Button>
        </DialogActions>
      </Dialog>

      {/* Feedback Dialog */}
      <Dialog open={feedbackDialogOpen} onClose={() => setFeedbackDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Submit Feedback</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Rating</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <IconButton
                    key={star}
                    onClick={() => setFeedbackForm({ ...feedbackForm, rating: star })}
                    color={star <= feedbackForm.rating ? 'primary' : 'default'}
                  >
                    <StarIcon />
                  </IconButton>
                ))}
              </Box>
            </Box>

            <TextField
              label="Category"
              select
              value={feedbackForm.category}
              onChange={(e) => setFeedbackForm({ ...feedbackForm, category: e.target.value })}
              fullWidth
              SelectProps={{ native: true }}
            >
              <option value="general">General Feedback</option>
              <option value="bug">Bug Report</option>
              <option value="feature">Feature Request</option>
              <option value="ui">User Interface</option>
              <option value="performance">Performance</option>
            </TextField>

            <TextField
              label="Your Feedback"
              multiline
              rows={4}
              value={feedbackForm.message}
              onChange={(e) => setFeedbackForm({ ...feedbackForm, message: e.target.value })}
              fullWidth
              required
              placeholder="Tell us what you think..."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFeedbackDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleFeedbackSubmit} variant="contained">Submit Feedback</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default HelpSupport;
